import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { usePollingFallback } from '@/hooks/usePollingFallback';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { formatMoney, formatDocDate, type CompanyLocale } from '@/utils/locale';
import { THREAD_VIEWED_PREFIX } from '@/types/omniChannel';

export type NotificationKind =
  | 'message'
  | 'lead'
  | 'job'
  | 'dispute'
  | 'estimate_accepted'
  | 'estimate_declined'
  | 'portal_message'
  // Technician app (Phase 2)
  | 'tech_declined'
  | 'tech_on_hold'
  | 'tech_quote_sent'
  | 'job_closed_by_tech'
  // Accounting sync
  | 'xero_sync_issue';

export interface NotificationItem {
  id: string;
  kind: NotificationKind;
  title: string;
  description: string;
  /** Drives ordering and the unread comparison */
  timestamp: string;
  /** In-app route to open when the row is clicked */
  href: string;
}

/** Timestamp of the last time the user opened the notifications panel. */
const LAST_SEEN_KEY = 'notifications_last_seen';

export const getNotificationsLastSeen = (): Date => {
  try {
    const stored = localStorage.getItem(LAST_SEEN_KEY);
    return stored ? new Date(stored) : new Date(0);
  } catch {
    return new Date(0);
  }
};

export const markNotificationsSeen = () => {
  try {
    localStorage.setItem(LAST_SEEN_KEY, new Date().toISOString());
  } catch {
    // Private browsing / storage disabled — the badge just won't persist.
  }
};

// Inbound SMS newer than the thread's last-viewed marker, mirroring the
// per-thread localStorage convention used by the omni-channel inbox.
const fetchUnreadMessages = async (): Promise<NotificationItem[]> => {
  // The 30-day cap prevents a CTM historical backfill from flooding the panel.
  const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
  const { data, error } = await supabase
    .from('sms_messages')
    .select('id, sent_at, created_at, content, from_number, lead_id, customer_id, tracking_number')
    .eq('direction', 'inbound')
    .gte('sent_at', thirtyDaysAgo)
    .order('sent_at', { ascending: false })
    .limit(200);

  if (error) throw error;

  const threadLatestViewed = new Map<string, Date>();
  const items: NotificationItem[] = [];

  for (const msg of data || []) {
    const threadKey = msg.lead_id
      ? `lead:${msg.lead_id}:${msg.tracking_number || 'default'}`
      : msg.customer_id
        ? `customer:${msg.customer_id}:${msg.tracking_number || 'default'}`
        : null;

    if (!threadKey) continue;

    if (!threadLatestViewed.has(threadKey)) {
      let lastViewed: string | null = null;
      try {
        lastViewed = localStorage.getItem(`${THREAD_VIEWED_PREFIX}${threadKey}`);
      } catch {
        lastViewed = null;
      }
      threadLatestViewed.set(threadKey, lastViewed ? new Date(lastViewed) : new Date(0));
    }

    const sentAt = msg.sent_at || msg.created_at;
    if (new Date(sentAt) <= threadLatestViewed.get(threadKey)!) continue;

    items.push({
      id: `message:${msg.id}`,
      kind: 'message',
      title: `New message from ${msg.from_number || 'unknown number'}`,
      description: msg.content?.trim() || 'No message body',
      timestamp: sentAt,
      href: '/communications?tab=omni-channel',
    });
  }

  return items;
};

const fetchRecentLeads = async (): Promise<NotificationItem[]> => {
  const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
  const { data, error } = await supabase
    .from('leads')
    .select('id, customer_name, issue_description, created_at, status')
    .neq('status', 'spam')
    .gte('created_at', since)
    .order('created_at', { ascending: false })
    .limit(50);

  if (error) throw error;

  return (data || []).map((lead) => ({
    id: `lead:${lead.id}`,
    kind: 'lead' as const,
    title: `New lead — ${lead.customer_name || 'Unknown caller'}`,
    description: lead.issue_description?.trim() || 'No description',
    timestamp: lead.created_at,
    href: `/leads/${lead.id}`,
  }));
};

// Jobs already on the schedule that still have nobody assigned to run them.
const fetchUnassignedJobs = async (): Promise<NotificationItem[]> => {
  const { data, error } = await supabase
    .from('jobs')
    .select('id, job_number, customer_name, scheduled_date, status, created_at')
    .in('status', ['scheduled', 'in_progress', 'on_hold', 'quote_sent'])
    .is('assigned_tech_id', null)
    .order('created_at', { ascending: false })
    .limit(50);

  if (error) throw error;

  return (data || []).map((job) => ({
    id: `job:${job.id}`,
    kind: 'job' as const,
    title: `Job ${job.job_number} needs a tech`,
    description: `${job.customer_name || 'Unknown customer'} — no technician assigned`,
    timestamp: job.created_at,
    href: `/jobs/${job.id}`,
  }));
};

// Open chargebacks: the money is already withheld and there is an evidence deadline.
const fetchOpenDisputes = async (locale: CompanyLocale, timezone: string): Promise<NotificationItem[]> => {
  const { data, error } = await supabase
    .from('invoices')
    .select('id, invoice_number, customer_name, dispute_amount, dispute_due_at, dispute_reason, updated_at')
    .eq('dispute_state', 'open')
    .order('updated_at', { ascending: false })
    .limit(20);

  if (error) throw error;

  return (data || []).map((inv) => {
    const due = inv.dispute_due_at ? formatDocDate(inv.dispute_due_at, locale.shortDate, timezone) : null;
    const amount = formatMoney(Number(inv.dispute_amount ?? 0), locale);
    return {
      id: `dispute:${inv.id}`,
      kind: 'dispute' as const,
      title: `Payment disputed — ${inv.invoice_number}`,
      description: `${inv.customer_name || 'Customer'} · ${amount}${due ? ` · evidence due ${due}` : ''}`,
      timestamp: inv.updated_at,
      href: `/invoices/${inv.id}`,
    };
  });
};

// Customers answering estimates from the public link: accepted (signed) or declined.
const fetchEstimateOutcomes = async (locale: CompanyLocale): Promise<NotificationItem[]> => {
  const since = new Date(Date.now() - 7 * 24 * 60 * 60_000).toISOString();
  const { data, error } = await supabase
    .from('estimates')
    .select('id, estimate_number, customer_name, estimate_amount, status, accepted_at, declined_at, signer_name, declined_by, decliner_name, decline_reason')
    .in('status', ['accepted', 'declined'])
    .gte('updated_at', since)
    .order('updated_at', { ascending: false })
    .limit(20);

  if (error) throw error;

  return (data || []).flatMap((est): NotificationItem[] => {
    const amount = formatMoney(Number(est.estimate_amount ?? 0), locale);
    if (est.status === 'accepted' && est.accepted_at) {
      return [{
        id: `estimate-accepted:${est.id}`,
        kind: 'estimate_accepted' as const,
        title: `Estimate ${est.estimate_number} accepted`,
        description: `${est.signer_name || est.customer_name || 'Customer'} signed · ${amount}`,
        timestamp: est.accepted_at,
        href: `/estimates/${est.id}`,
      }];
    }
    // Only customer declines are news; staff already know what they marked
    if (est.status === 'declined' && est.declined_at && est.declined_by === 'customer') {
      return [{
        id: `estimate-declined:${est.id}`,
        kind: 'estimate_declined' as const,
        title: `Estimate ${est.estimate_number} declined`,
        description: `${est.decliner_name || est.customer_name || 'Customer'} · ${amount}${est.decline_reason ? ` · "${est.decline_reason}"` : ''}`,
        timestamp: est.declined_at,
        href: `/estimates/${est.id}`,
      }];
    }
    return [];
  });
};

// Messages customers send from the portal (customer_communications, type 'portal').
interface PortalMessageRow {
  id: string;
  customer_id: string | null;
  subject: string | null;
  content: string | null;
  created_at: string;
  customer: { customer_name: string | null } | null;
}

const fetchPortalMessages = async (): Promise<NotificationItem[]> => {
  const since = new Date(Date.now() - 7 * 24 * 60 * 60_000).toISOString();
  const { data, error } = await supabase
    .from('customer_communications')
    .select('id, customer_id, subject, content, created_at, customer:customers(customer_name)')
    .eq('communication_type', 'portal')
    .eq('direction', 'inbound')
    .gte('created_at', since)
    .order('created_at', { ascending: false })
    .limit(20);

  if (error) throw error;

  return ((data ?? []) as unknown as PortalMessageRow[]).map((row) => ({
    id: `portal-message:${row.id}`,
    kind: 'portal_message' as const,
    title: `Message from ${row.customer?.customer_name || 'a customer'}`,
    description: row.content?.trim() || row.subject || 'Portal message',
    timestamp: row.created_at,
    href: row.customer_id ? `/customers/${row.customer_id}?tab=communications` : '/customers',
  }));
};

// ---------------------------------------------------------------------------
// Technician app events (Phase 2 columns / RPCs; see 20260912100400..100900)
// ---------------------------------------------------------------------------

const LIVE_JOB_STATUSES = ['scheduled', 'in_progress', 'on_hold', 'quote_sent'];

// The technician declined the assignment from the app. The job keeps assigned_tech_id,
// so the "needs a tech" fetch above never sees it; the office must reassign.
const fetchTechDeclined = async (): Promise<NotificationItem[]> => {
  const { data, error } = await supabase
    .from('jobs')
    .select('id, job_number, customer_name, tech_ack_at, tech_decline_reason, techs(name)')
    .eq('tech_ack_status', 'declined')
    .in('status', LIVE_JOB_STATUSES)
    .order('tech_ack_at', { ascending: false })
    .limit(50);

  if (error) throw error;

  return (data || []).map((job) => {
    const tech = (job.techs as { name: string } | null)?.name || 'The technician';
    return {
      id: `tech-declined:${job.id}`,
      kind: 'tech_declined' as const,
      title: `${tech} declined job ${job.job_number}`,
      description: `${job.customer_name || 'Unknown customer'}${job.tech_decline_reason ? ` · "${job.tech_decline_reason}"` : ' · no reason given'} · reassign it`,
      timestamp: job.tech_ack_at ?? new Date().toISOString(),
      href: `/jobs/${job.id}`,
    };
  });
};

// Put on hold from the field, with the reason the technician typed.
const fetchTechOnHold = async (): Promise<NotificationItem[]> => {
  const { data, error } = await supabase
    .from('jobs')
    .select('id, job_number, customer_name, hold_reason, updated_at, techs(name)')
    .eq('status', 'on_hold')
    .not('hold_reason', 'is', null)
    .order('updated_at', { ascending: false })
    .limit(50);

  if (error) throw error;

  return (data || []).map((job) => {
    const tech = (job.techs as { name: string } | null)?.name || 'The technician';
    return {
      id: `tech-on-hold:${job.id}`,
      kind: 'tech_on_hold' as const,
      title: `${tech} put job ${job.job_number} on hold`,
      description: `${job.customer_name || 'Unknown customer'} · "${job.hold_reason}"`,
      timestamp: job.updated_at,
      href: `/jobs/${job.id}`,
    };
  });
};

// Quotes a technician raised and sent for signature from the phone (still open).
const fetchTechQuotesSent = async (locale: CompanyLocale): Promise<NotificationItem[]> => {
  const { data: techs, error: techErr } = await supabase
    .from('techs')
    .select('name, user_id')
    .not('user_id', 'is', null);
  if (techErr) throw techErr;
  const techByUser = new Map((techs ?? []).map((t) => [t.user_id as string, t.name]));
  if (techByUser.size === 0) return [];

  const since = new Date(Date.now() - 7 * 24 * 60 * 60_000).toISOString();
  const { data, error } = await supabase
    .from('estimates')
    .select('id, estimate_number, customer_name, estimate_amount, status, sent_at, created_at, created_by')
    .in('status', ['sent', 'viewed'])
    .in('created_by', [...techByUser.keys()])
    .gte('created_at', since)
    .order('created_at', { ascending: false })
    .limit(20);

  if (error) throw error;

  return (data || []).map((est) => ({
    id: `tech-quote-sent:${est.id}`,
    kind: 'tech_quote_sent' as const,
    title: `Quote ${est.estimate_number} sent from the field`,
    description: `${techByUser.get(est.created_by ?? '') || 'Technician'} · ${est.customer_name || 'Customer'} · ${formatMoney(Number(est.estimate_amount ?? 0), locale)}${est.status === 'viewed' ? ' · viewed' : ''}`,
    timestamp: est.sent_at ?? est.created_at ?? new Date().toISOString(),
    href: `/estimates/${est.id}`,
  }));
};

// close_job from the app: audit_logs action='job_closed' with metadata.source='tech_app'.
interface JobClosedMetadata {
  invoice_id?: string;
  invoice_total?: number | string;
  payments?: number;
  signed?: boolean;
  source?: string;
  job_number?: string;
}

const fetchJobsClosedByTech = async (locale: CompanyLocale): Promise<NotificationItem[]> => {
  const since = new Date(Date.now() - 7 * 24 * 60 * 60_000).toISOString();
  const { data, error } = await supabase
    .from('audit_logs')
    .select('id, entity_id, metadata, created_at')
    .eq('action', 'job_closed')
    .eq('entity', 'job')
    .eq('metadata->>source', 'tech_app')
    .gte('created_at', since)
    .order('created_at', { ascending: false })
    .limit(20);

  if (error) throw error;

  return (data || []).map((row) => {
    const m = (row.metadata ?? {}) as JobClosedMetadata;
    const total = m.invoice_total !== undefined ? formatMoney(Number(m.invoice_total) || 0, locale) : null;
    return {
      id: `job-closed-by-tech:${row.id}`,
      kind: 'job_closed_by_tech' as const,
      title: `Job ${m.job_number ?? ''} closed from the app`.replace('  ', ' '),
      description: [total ? `Invoiced ${total}` : 'Invoiced', m.signed ? 'customer signed' : 'no signature', m.payments ? `${m.payments} payment${m.payments === 1 ? '' : 's'} taken` : null]
        .filter(Boolean).join(' · '),
      timestamp: row.created_at,
      href: row.entity_id ? `/jobs/${row.entity_id}` : '/jobs',
    };
  });
};

// ---------------------------------------------------------------------------
// Xero sync: pushes that failed or need a human (admin-only RPC; everyone
// else gets an empty list rather than an error).
// ---------------------------------------------------------------------------

const XERO_ISSUE_TITLES: Record<string, string> = {
  failed: 'Xero sync failed',
  dead: 'Xero sync gave up',
  skipped: 'Xero needs a decision',
  drift: 'Invoice differs in Xero',
  locked: 'Invoice locked in Xero',
  error: 'Xero sync error',
  orphaned: 'Missing in Xero',
};

const fetchXeroSyncIssues = async (): Promise<NotificationItem[]> => {
  const { data, error } = await supabase.rpc('get_xero_sync_issues', { p_limit: 20 });
  if (error) {
    if (/only admins/i.test(error.message) || /permission denied/i.test(error.message)) return [];
    throw error;
  }
  const since = Date.now() - 7 * 24 * 60 * 60_000;
  return ((data ?? []) as Array<{ kind: string; id: string; entity_type: string; label: string | null; state: string; detail: string | null; occurred_at: string }>)
    .filter((row) => new Date(row.occurred_at).getTime() >= since)
    .map((row) => ({
      id: `xero-issue:${row.kind}:${row.id}`,
      kind: 'xero_sync_issue' as const,
      title: `${XERO_ISSUE_TITLES[row.state] ?? 'Xero sync issue'}${row.label ? ` — ${row.label}` : ''}`,
      description: row.detail?.trim() || `${row.entity_type} could not be synced`,
      timestamp: row.occurred_at,
      href: '/settings?tab=integrations&xero=issues',
    }));
};

export interface NotificationsData {
  items: NotificationItem[];
  unreadCount: number;
}

export const useNotifications = () => {
  const refetchInterval = usePollingFallback(5 * 60_000);
  const { locale, timezone } = useCompanyLocale();

  return useQuery<NotificationsData>({
    // Money/date strings are baked into the items, so re-run when the tenant locale resolves.
    queryKey: ['notifications', locale.countryCode, timezone],
    queryFn: async () => {
      const [messages, leads, jobs, disputes, estimates, portalMessages, techDeclined, techOnHold, techQuotes, closedByTech, xeroIssues] = await Promise.all([
        fetchUnreadMessages(),
        fetchRecentLeads(),
        fetchUnassignedJobs(),
        fetchOpenDisputes(locale, timezone),
        fetchEstimateOutcomes(locale),
        fetchPortalMessages(),
        fetchTechDeclined(),
        fetchTechOnHold(),
        fetchTechQuotesSent(locale),
        fetchJobsClosedByTech(locale),
        fetchXeroSyncIssues(),
      ]);

      const items = [
        ...disputes, ...xeroIssues, ...techDeclined, ...techOnHold, ...estimates, ...techQuotes, ...closedByTech,
        ...portalMessages, ...messages, ...leads, ...jobs,
      ].sort(
        (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );

      const lastSeen = getNotificationsLastSeen();
      const unreadCount = items.filter((i) => new Date(i.timestamp) > lastSeen).length;

      return { items: items.slice(0, 50), unreadCount };
    },
    staleTime: 30_000,
    refetchInterval,
  });
};
